// @ts-check
// =============================================================================
// js/screens/splash.js — the start screen: split-logo, 18+ legal gate, and
// entry buttons (plan/splash.md; old client RenderStart :500).
//
// FLOW
//   First visit          → logo + Terms of Service + Yes/No. "No" leaves the
//                          site (pbskids, as tradition demands). "Yes" stores
//                          localStorage.eighteen and starts login resolution.
//   Returning visitor    → gate already accepted: silent login kicks off
//                          immediately (splash shows "Loading" until boot.js
//                          routes into the lobby).
//   #/splash post-login  → the context-dependent button set (guests: Join/
//                          Login/Learn/View Cards/Spectate; members: Play
//                          Lobby/Cardpool|Collection/Settings).
//
// The 18+ decision is LOAD-BEARING: accepting is the precondition for all
// explicit content. Past that gate the game defaults to ZERO censorship
// (session.js guestSettings) — players dial content back in Settings.
// =============================================================================

import { store, subscribe } from '../core/store.js';
import { navigate } from '../core/router.js';
import { ensureSession, showLoginOptions, JOIN_URL } from '../app/session.js';
import { isQuickplay } from '../app/quickplay.js';
import { isShell } from '../app/shell-mode.js';
import { escapeHtml } from '../ui/modal.js';

/** Where declined visitors are sent (old LegalTerms(false) :748). */
const DECLINE_URL = 'https://pbskids.org/games';

/** Split-logo halves — self-hosted in public/gui/loading/ (no bucket dependency). */
const LOGO_TOP = '/gui/loading/Logo_Half_Top.png';
const LOGO_BOTTOM = '/gui/loading/Logo_Half_Bottom.png';

/** The Terms-of-Service text, ported verbatim (old `legal` :471). */
const LEGAL_HTML = `Welcome to Deckbiter!<br><br>

By accessing or using this game, you agree to comply with these Terms of Use. If you do not agree to these Terms, please refrain from using the service.<br><br>

This game contains adult content, including explicit sexual material, nudity, adult language, acts of violence, and other mature themes. You must be at least 18 years old, or the legal age of majority in your jurisdiction, to access or use this game. If you are not of legal age or accessing from a location where such content is prohibited, you are strictly forbidden from using this platform.<br><br>

All characters depicted in this game are fictional humans in costume and are portrayed as being over the age of 18. Any resemblance to actual persons, living or dead, is purely coincidental.<br><br>

You agree not to reproduce, distribute, publicly display, modify, or create derivative works from any part of the game or its content without prior written authorization from Deckbiter’s creators. All rights not expressly granted are reserved.<br><br>

You must keep your login credentials confidential and are solely responsible for all activities conducted through your account. You agree not to share, distribute, or otherwise expose the game’s content to minors or unauthorized third parties.<br><br>

You agree to comply with all applicable laws and regulations when using this game. You are prohibited from uploading, sharing, or distributing any content that is illegal, infringing, obscene, defamatory, or otherwise prohibited. You must not use the service to harass, abuse, or harm others.<br><br>

Deckbiter reserves the right to suspend or terminate any account, remove any content, or restrict access to the platform at any time and without prior notice, for conduct that violates these Terms or is otherwise harmful to the service or its users.<br><br>

You agree to indemnify and hold harmless Deckbiter, its developers, affiliates, and licensors from any claims, liabilities, damages, losses, and expenses, including reasonable legal fees, arising out of or related to your violation of these Terms or misuse of the game.<br><br>

Deckbiter is provided “as is” and without warranties of any kind, whether express or implied. To the fullest extent permitted by law, Deckbiter disclaims all warranties, including but not limited to merchantability, fitness for a particular purpose, and non-infringement. Your use of the game is at your sole risk.<br><br>

To the maximum extent permitted by law, Deckbiter and its creators will not be liable for any indirect, incidental, consequential, or punitive damages arising out of or relating to your use or inability to use the game.<br><br>

These Terms are governed by and construed under the laws of the jurisdiction in which Deckbiter’s principal operations are based, without regard to conflict of laws principles. You agree that any disputes arising under these Terms will be subject to the exclusive jurisdiction of the competent courts of that jurisdiction.<br><br>

If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.<br><br>

Deckbiter reserves the right to modify these Terms at any time. Updated Terms will be posted within the game. Your continued use of the service after any changes constitutes your acceptance of the revised Terms.<br><br>`;

/** @type {(() => void)|null} store unsubscriber while mounted */
let stopSession = null;

/** @type {number|null} the wiggle animation frame handle */
let wiggleFrame = null;

/**
 * Render the splash into `container`.
 * @param {HTMLElement} container
 * @param {Record<string,string>} _params
 * @returns {void}
 */
export function render(container, _params) {
	const eighteen = !!localStorage.getItem('eighteen');

	const screen = document.createElement('div');
	screen.className = 'startScreen';
	screen.innerHTML = `
		<div class="startScreenLogo">
			<img id="logoCrestBottom" class="startScreenLogoImg" src="${LOGO_BOTTOM}" alt="">
			<img id="logoCrestTop" class="startScreenLogoImg" src="${LOGO_TOP}" alt="Deckbiter">
		</div>`;

	const content = document.createElement('div');
	content.className = 'startScreenButtons';
	screen.appendChild(content);

	container.innerHTML = '';
	container.appendChild(screen);
	startLogoWiggle(screen);

	if (!eighteen) {
		renderLegalGate(content);
	} else {
		ensureSession();
		renderPostGate(content);
		// Re-render the button set when login resolves.
		stopSession?.();
		stopSession = subscribe('session', () => renderPostGate(content));
	}
}

/** Tear down subscriptions + the wiggle loop. */
export function destroy() {
	stopSession?.();
	stopSession = null;
	if (wiggleFrame !== null) {
		cancelAnimationFrame(wiggleFrame);
		wiggleFrame = null;
	}
}

/**
 * The 18+ Terms-of-Service gate (old LegalTerms :748).
 * @param {HTMLElement} content
 */
function renderLegalGate(content) {
	content.innerHTML = `
		<div class="legalBullshit">${LEGAL_HTML}</div>
		<div class="legalQuestion">Are you eighteen years old and agree to the terms of service?</div>
		<div>
			<button class="legalButton" id="legal-yes">Yes</button>
			<button class="legalButton" id="legal-no">No</button>
		</div>`;
	content.querySelector('#legal-yes')?.addEventListener('click', () => {
		localStorage.setItem('eighteen', 'true');
		ensureSession();
		renderPostGate(content);
		stopSession?.();
		stopSession = subscribe('session', () => renderPostGate(content));
	});
	content.querySelector('#legal-no')?.addEventListener('click', () => {
		window.location.href = DECLINE_URL;
	});
}

/**
 * Post-gate content: "Loading" while login resolves; the context-dependent
 * button set once a session exists (old RenderStart :544–609).
 * @param {HTMLElement} content
 */
function renderPostGate(content) {
	const { session, userData } = store;
	if (session.phase !== 'ready' || !userData) {
		content.innerHTML = `<div style="color:var(--text-dim)">Loading&hellip;</div>`;
		return;
	}

	// Quickplay never shows the button set: boot is already starting the bot
	// duel, this screen just holds the stage until the game lands.
	if (isQuickplay()) {
		content.innerHTML = `<div style="color:var(--text-dim)">Starting your game&hellip;</div>`;
		return;
	}

	content.innerHTML = '';
	/** @type {Array<[string, () => void]>} */
	let buttons;
	if (session.isGuest && isShell()) {
		// Shell (itch etc.): guest-only bot play. No Login; Join opens Patreon in
		// a REAL tab (a same-window redirect would strand the iframe on a
		// page that refuses to be framed) — subscribe there, then log in on
		// the full site.
		buttons = [
			['Duel A Bot', () => navigate('challenge')],
			['Join', () => window.open(JOIN_URL, '_blank', 'noopener')],
			['Learn To Play', () => navigate('help')],
			['View Cards', () => navigate('gallery')],
			['Spectate', () => navigate('lobby')],
		];
	} else if (session.isGuest) {
		buttons = [
			['Duel A Bot', () => navigate('challenge')],
			['Join', () => { window.location.href = JOIN_URL; }],
			['Login', showLoginOptions],
			['Learn To Play', () => navigate('help')],
			['View Cards', () => navigate('gallery')],
			['Spectate', () => navigate('lobby')],
		];
	} else {
		buttons = [
			['Play Lobby', () => navigate('lobby')],
			[session.tier === 'paid' ? 'Cardpool' : 'Collection', () => navigate('gallery')],
			['Settings', () => navigate('settings')],
		];
	}
	for (const [label, onClick] of buttons) {
		const btn = document.createElement('button');
		btn.className = 'defaultButton';
		btn.textContent = label;
		btn.style.display = 'block';
		btn.style.margin = '8px auto';
		btn.addEventListener('click', onClick);
		content.appendChild(btn);
	}
	// Guests get a hint of who they are (nickname is auto-generated).
	if (session.isGuest) {
		const hint = document.createElement('div');
		hint.style.color = 'var(--text-dim)';
		hint.style.fontSize = '13px';
		hint.textContent = `Playing as guest "${escapeHtml(userData.nickname)}"`;
		content.appendChild(hint);
	}
}

// -----------------------------------------------------------------------------
// The mouse-reactive wiggling split logo (old LogoWiggle/LogoWiggleLoop :630).
// The two logo halves spring around the pointer with damped velocity; the
// bottom half moves opposite the top for the "bite" effect.
// -----------------------------------------------------------------------------

/**
 * Attach pointer listeners + start the spring loop.
 * @param {HTMLElement} screen  The .startScreen element.
 */
function startLogoWiggle(screen) {
	const spring = { oldX: 0, oldY: 0, velX: 0, velY: 0, posX: 0, posY: 0, mouseX: 0, mouseY: 0 };

	const onMove = (/** @type {MouseEvent|TouchEvent} */ e) => {
		const point = 'touches' in e && e.touches.length ? e.touches[0] : /** @type {MouseEvent} */ (e);
		spring.mouseX = point.clientX;
		spring.mouseY = point.clientY;
		// Tamed (owner: the old 50/0.2 got "crazy" on fast mouse sweeps): less
		// energy per move event, blended in more gently.
		const maxDelta = 18;
		const dx = Math.max(Math.min(point.clientX - spring.oldX, maxDelta), -maxDelta);
		const dy = Math.max(Math.min(point.clientY - spring.oldY, maxDelta), -maxDelta);
		const blend = 0.12;
		spring.velX = spring.velX * (1 - blend) + dx * blend;
		spring.velY = spring.velY * (1 - blend) + dy * blend;
		spring.oldX = point.clientX;
		spring.oldY = point.clientY;
	};
	screen.addEventListener('mousemove', onMove);
	screen.addEventListener('touchmove', onMove);

	const loop = () => {
		const top = document.getElementById('logoCrestTop');
		const bottom = document.getElementById('logoCrestBottom');
		if (!top || !bottom) {
			wiggleFrame = null;
			return; // splash left the stage — stop looping
		}

		// Greatly reduced from the original 0.95/400/45°/0.4: the spring now
		// settles in about a second and the halves never fly off the crest.
		const damping = 0.88;
		const maxOffset = 100;
		const springStrength = 0.007;
		const maxRotation = 8;
		const scale = 0.25;

		// Amplify the pull when the pointer is near the logo.
		const rect = top.getBoundingClientRect();
		const dx = spring.mouseX - (rect.left + rect.width / 2);
		const dy = spring.mouseY - (rect.top + rect.height / 2);
		const amplification = 1 - Math.min(Math.hypot(dx, dy) / 900, 1);

		spring.velX += -spring.posX * springStrength * (1 + amplification);
		spring.velY += -spring.posY * springStrength * (1 + amplification);
		spring.velX *= damping;
		spring.velY *= damping;
		spring.posX = Math.max(Math.min(spring.posX + spring.velX, maxOffset), -maxOffset);
		spring.posY = Math.max(Math.min(spring.posY + spring.velY, maxOffset), -maxOffset);

		const rotation = Math.max(Math.min(spring.posX * 0.1, maxRotation), -maxRotation);
		top.style.transform =
			`translate(${spring.posX * scale}px, ${spring.posY * scale}px) rotate(${rotation}deg)`;
		bottom.style.transform =
			`translate(${-spring.posX * scale * 0.5}px, ${-spring.posY * scale * 0.5}px) rotate(${-rotation * 0.5}deg)`;

		wiggleFrame = requestAnimationFrame(loop);
	};
	wiggleFrame = requestAnimationFrame(loop);
}
